"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import { ArrowLeft } from "lucide-react";

interface LeaderboardEntry {
    rank: number;
    userId: string;
    username: string;
    displayName: string;
    avatarUrl: string;
    totalEarnings: number;
    gamesWon: number;
    totalGames: number;
}

const BACKEND_URL = process.env.NEXT_PUBLIC_API_URL || 'https://drop-backend-65296643664.us-central1.run.app';

export default function LeaderboardPage() {
    const [leaders, setLeaders] = useState<LeaderboardEntry[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchLeaders = async () => {
            try {
                const res = await fetch(`${BACKEND_URL}/api/v1/leaderboard`);
                if (res.ok) {
                    const data = await res.json();
                    setLeaders(data);
                }
            } catch (err) {
                console.error("Failed to fetch leaderboard", err);
            } finally {
                setLoading(false);
            }
        };

        fetchLeaders();
    }, []);

    return (
        <div className="min-h-screen bg-[#F8F7F4] text-gray-900 font-sans p-4 md:p-8">
            <div className="max-w-3xl mx-auto">

                {/* Header */}
                <div className="flex items-center gap-4 mb-8">
                    <Link href="/" className="w-10 h-10 rounded-full bg-white shadow-sm flex items-center justify-center text-gray-600 hover:bg-gray-50 transition-colors">
                        <ArrowLeft size={20} />
                    </Link>
                    <div>
                        <h1 className="text-3xl font-black text-gray-900">Leaderboard</h1>
                        <p className="text-gray-500 font-medium">Top High Rollers</p>
                    </div>
                </div>

                {/* List */}
                <div className="bg-white rounded-3xl shadow-sm border border-gray-100 overflow-hidden">
                    {loading ? (
                        <div className="p-8 text-center text-gray-400">Loading top players...</div>
                    ) : leaders.length > 0 ? (
                        <div className="divide-y divide-gray-100">
                            {leaders.map((player) => (
                                <div key={player.userId} className="p-4 md:p-6 flex items-center gap-4 hover:bg-gray-50 transition-colors">
                                    {/* Rank */}
                                    <div className={`w-10 h-10 flex-shrink-0 flex items-center justify-center font-black text-xl rounded-full ${player.rank === 1 ? 'bg-yellow-100 text-yellow-600' :
                                            player.rank === 2 ? 'bg-gray-100 text-gray-600' :
                                                player.rank === 3 ? 'bg-orange-100 text-orange-600' :
                                                    'text-gray-400'
                                        }`}>
                                        {player.rank}
                                    </div>

                                    {/* Avatar */}
                                    <div className="w-12 h-12 rounded-full bg-gray-200 overflow-hidden flex-shrink-0">
                                        {player.avatarUrl ? (
                                            <img src={player.avatarUrl} alt={player.displayName} className="w-full h-full object-cover" />
                                        ) : (
                                            <div className="w-full h-full flex items-center justify-center bg-indigo-100 text-indigo-600 font-bold">
                                                {player.displayName?.[0] || 'U'}
                                            </div>
                                        )}
                                    </div>

                                    {/* Info */}
                                    <div className="flex-1 min-w-0">
                                        <div className="font-bold text-gray-900 truncate">{player.displayName}</div>
                                        <div className="text-xs text-gray-400 font-medium truncate">@{player.username} • {player.gamesWon} wins</div>
                                    </div>

                                    {/* Earnings */}
                                    <div className="text-right">
                                        <div className="font-black text-green-600 text-lg">£{player.totalEarnings.toFixed(2)}</div>
                                        <div className="text-xs text-gray-400 font-medium">EARNED</div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="p-8 text-center text-gray-400">No stats recorded yet.</div>
                    )}
                </div>

            </div>
        </div>
    );
}
